import { PERMISSIONS } from "@/constants/permissions"
import { useGuardedMutation } from "@/hooks/useGuardedMutation"
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { apiClientV2 } from "../../../services/api/apiClient"
import {
  mcCursorList,
  mcCursorListKeepPrevious,
  useMcPatch,
} from "./mcQueryHelpers"
import type {
  ComplianceEventOut,
  CustomerOut,
  CustomerRow,
  CustomerUpdateIn,
  ProductRow,
} from "./types/masterData"

const BASE = "/material-compliance/master-data/customers"

// ─── List ─────────────────────────────────────────────────────────────────────

export function useCustomerList(
  params: {
    q?: string
    product_id?: string
    article_id?: string
    supplier_id?: string
    sort?: string
    limit?: number
  } = {},
) {
  return useInfiniteQuery(
    mcCursorListKeepPrevious<CustomerRow>(
      ["mc", "customers", "list", params],
      BASE,
      params,
    ),
  )
}

// ─── Detail ───────────────────────────────────────────────────────────────────

export function useCustomerDetail(id: string | null) {
  return useQuery({
    queryKey: ["mc", "customers", "detail", id],
    queryFn: () =>
      apiClientV2.get<CustomerOut>(`${BASE}/${id}`).then((r) => r.data),
    enabled: !!id,
  })
}

// ─── Sub-resource: products of this customer ──────────────────────────────────

export function useCustomerProducts(
  id: string | null,
  params: { limit?: number } = {},
) {
  return useInfiniteQuery(
    mcCursorList<ProductRow>(
      ["mc", "customers", id, "products", params],
      `${BASE}/${id}/products`,
      params,
      !!id,
    ),
  )
}

// ─── Sub-resource: audit trail ────────────────────────────────────────────────

export function useCustomerAudit(
  id: string | null,
  params: { limit?: number } = {},
) {
  return useInfiniteQuery(
    mcCursorList<ComplianceEventOut>(
      ["mc", "customers", id, "audit", params],
      `${BASE}/${id}/audit`,
      params,
      !!id,
    ),
  )
}

// ─── Mutation ─────────────────────────────────────────────────────────────────

export function usePatchCustomer() {
  return useMcPatch<CustomerOut, CustomerUpdateIn>("customers", BASE)
}

// ─── Product mappings — "products bought" (conformity v2, Q8) ────────────────

// The mapping routes live on the customers router, not master-data.
const MAPPING_BASE = "/material-compliance/customers"

export interface CustomerProductMapping {
  productId: string
  productNumber: string
  name: string | null
  customerProductNumber: string | null
  soldAsIs: boolean
  sourceArticleId: string | null
  scipNumber: string | null
}

export function useCustomerProductMappings(customerId: string | null) {
  return useQuery({
    queryKey: ["mc", "customers", customerId, "product-mappings"],
    queryFn: () =>
      apiClientV2
        .get<CustomerProductMapping[]>(`${MAPPING_BASE}/${customerId}/products`)
        .then((r) => r.data),
    enabled: !!customerId,
  })
}

export function useUpsertCustomerProductMapping() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      customerId,
      productId,
      customerProductNumber,
    }: {
      customerId: string
      productId: string
      customerProductNumber: string | null
    }) =>
      apiClientV2
        .put<CustomerProductMapping>(
          `${MAPPING_BASE}/${customerId}/products/${productId}`,
          { customerProductNumber },
        )
        .then((r) => r.data),
    onSuccess: (_data, { customerId }) => {
      qc.invalidateQueries({
        queryKey: ["mc", "customers", customerId, "product-mappings"],
      })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_CUSTOMERS_EDIT)
}

export function useDeleteCustomerProductMapping() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      customerId,
      productId,
    }: {
      customerId: string
      productId: string
    }) =>
      apiClientV2.delete(`${MAPPING_BASE}/${customerId}/products/${productId}`),
    onSuccess: (_data, { customerId }) => {
      qc.invalidateQueries({
        queryKey: ["mc", "customers", customerId, "product-mappings"],
      })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_CUSTOMERS_EDIT)
}
